import React, { lazy, createElement } from "react";
import { RouteErrorFallback } from "./shared/RouteErrorFallback";

const routeErrorFallback = createElement(RouteErrorFallback);

// --- Lazy-loaded views ---

// Auth
const LoginView = lazy(() => import("./auth/LoginView"));
const ForgotPasswordForm = lazy(() => import("./auth/ForgotPasswordForm"));
const VerifyUser = lazy(() => import("./auth/VerifyUser"));
const ChangePasswordForm = lazy(() => import("./auth/ChangePasswordForm"));

// Product
const GalleryView = lazy(() => import("./product/GalleryView"));
const CategoryView = lazy(() => import("./product/CategoryView"));
const EditorView = lazy(() => import("./product/EditorView"));
const SearchView = lazy(() => import("./product/searchView"));
const RitaProductView = lazy(() => import("./product/RitaProductView"));
const FotoProductView = lazy(() => import("./product/FotoProductView"));
const SelectColorMaterialView = lazy(() => import("./product/selectColorMaterialView"));

// Product detail
const WaterDrainageDetailView = lazy(() => import("./water-drainage"));
const InfastingDetailView = lazy(() => import("./infasting"));
const OvrigtDetailView = lazy(() => import("./ovrigt"));

// Project
const OverviewView = lazy(() => import("./project/OverviewView"));
const ProjectView = lazy(() => import("./project/ProjectView"));
const MapViewComponent = lazy(() => import("./project/MapComponent"));
const SummaryView = lazy(() => import("./project/SummaryView"));
const InstallningView = lazy(() => import("./project/InstallningView"));
const DocumentsView = lazy(() => import("./project/DocumentsView"));
const ImagesView = lazy(() => import("./project/ImagesView"));
const ProjectInformationView = lazy(() => import("./project/ProjectInformationView"));
const ContactPerson = lazy(() => import("./project/contactPerson"));

// Cart & order
const CartView = lazy(() => import("./cart/CartView"));
const UnsortedCartView = lazy(() => import("./cart/UnsortedCartView"));
const OrderModalView = lazy(() => import("./cart/OrderModal"));
const GuestOrderModalView = lazy(() => import("./cart/GuestOrderModal"));

// Settings
const SettingsView = lazy(() => import("./settings/SettingsView"));
const PersonaldataView = lazy(() => import("./settings/PersonaldataView"));
const CompanyinfoView = lazy(() => import("./settings/CompanyinfoView"));
const FinancesInfoView = lazy(() => import("./settings/FinancesInfoView"));
const StatistikInfoView = lazy(() => import("./settings/StatistikInfoView"));
const AllProjectSummaryView = lazy(() => import("./settings/AllProjectSummaryView"));
const InställningspostView = lazy(() => import("./settings/Inställningspost"));
const InställningsGraderView = lazy(() => import("./settings/InställningsGrader"));
const UserTermsAndGuidelines = lazy(() => import("./settings/userTermsAndGuidelines"));
const LanguageSelectionPage = lazy(() => import("./settings/LanguageSelectionPage"));

// Admin
const AdminView = lazy(() => import("./admin/AdminView"));
const AdminCompanyView = lazy(() => import("./admin/AdminCompanyView"));
const AdminProductView = lazy(() => import("./admin/sheet-product/AdminProductView"));
const AdminProductPricesView = lazy(() => import("./admin/AdminProductPricesView"));
const AdminEditorView = lazy(() => import("./admin/sheet-product/AdminEditorView"));
const AdminFunctionsView = lazy(() => import("./admin/functions/AdminFunctionsView"));
const AdminFunctionUpdateView = lazy(() => import("./admin/functions/AdminFunctionUpdateView"));
const AdminSelectedCompanyView = lazy(() => import("./admin/AdminSelectedCompanyView"));
const AdminCompanyUserView = lazy(() => import("./admin/AdminCompanyUserView"));
const AdminCompanyInfoView = lazy(() => import("./admin/AdminCompanyInfoView"));
const AdminInstallningView = lazy(() => import("./admin/installningView"));
const AdminFinancesInfoView = lazy(() => import("./admin/AdminFinancesInfoView"));
const InstallningCompanyUserView = lazy(() => import("./admin/InstallningCompanyUserView"));
const AdminInfastingUpdateView = lazy(() => import("./admin/infasting/AdminInfastingUpdateView"));
const AdminOvrigtUpdateView = lazy(() => import("./admin/ovrigt/AdminOvrigtUpdateView"));
const AdminWaterdrainageUpdateView = lazy(() => import("./admin/waterdrainageproduct/AdminWaterdrainageUpdateView"));

// --- Route config types (v6 — routes are exact by default) ---

export type RouteEntry = {
	path: string;
	component: React.LazyExoticComponent<any> | React.ComponentType<any>;
	props?: Record<string, any>;
	/** If set, route only renders when guard returns true */
	guard?: (access: IUserTypeAccess) => boolean;
	/** Route-level error boundary fallback */
	errorElement?: React.ReactNode;
};

export type RedirectEntry = {
	from: string;
	to: string;
};

// --- Public routes (no auth required) ---

export const publicRoutes: RouteEntry[] = [
	{ path: "/", component: LoginView, props: { fixedOverscroll: true } },
	{ path: "/password-reset", component: ForgotPasswordForm },
	{ path: "/verify-email", component: VerifyUser },
	{ path: "/guestordermodal", component: GuestOrderModalView },
];

// --- Admin routes (shared between admin and superAdmin) ---

export const adminRoutes: RouteEntry[] = [
	{ path: "/admin-functions", component: AdminView },
	{ path: "/admin/products", component: AdminProductView },
	{ path: "/admin/selected-product", component: AdminEditorView },
	{ path: "/admin/vattenavrinningproducts", component: AdminWaterdrainageUpdateView },
	{ path: "/admin/infastingproducts", component: AdminInfastingUpdateView },
	{ path: "/admin/ovrigtproducts", component: AdminOvrigtUpdateView },
	{ path: "/admin/functions", component: AdminFunctionsView },
	{ path: "/admin/prod-functions", component: AdminFunctionUpdateView },
	{ path: "/admin/products-prices", component: AdminProductPricesView },
	{ path: "/admin/selected-företag", component: AdminSelectedCompanyView },
	{ path: "/admin/selected-anvandare", component: AdminCompanyUserView },
	{ path: "/admin/företagsuppgifter", component: AdminCompanyInfoView },
	{ path: "/admin/företag-settings", component: AdminInstallningView },
	{ path: "/admin/anvandare-settings", component: InstallningCompanyUserView },
	{ path: "/admin/financeinfo", component: AdminFinancesInfoView },
];

// Routes only available for superAdmin (juha@weatherproof.se)
export const superAdminExtraRoutes: RouteEntry[] = [
	{ path: "/admin/företag", component: AdminCompanyView },
];

// --- Customer routes (authenticated non-admin users) ---

export const customerRoutes: RouteEntry[] = [
	// Project
	{ path: "/project/list", component: OverviewView },
	{ path: "/project/details", component: ProjectView, errorElement: routeErrorFallback },
	{ path: "/project/map", component: MapViewComponent },
	{ path: "/project/summary", component: SummaryView },
	{ path: "/project/settings", component: InstallningView },
	{ path: "/project/documents", component: DocumentsView },
	{ path: "/project/images", component: ImagesView },
	{ path: "/project/cart", component: CartView, props: { scrollHeadlines: true }, errorElement: routeErrorFallback },
	{ path: "/project/unsorted", component: UnsortedCartView, props: { scrollHeadlines: true } },
	{ path: "/project/projectinformation", component: ProjectInformationView },
	{ path: "/project/contactperson", component: ContactPerson },
	{ path: "/project/material", component: SelectColorMaterialView },
	{ path: "/project/ordermodal/:type", component: OrderModalView },

	// Product
	{ path: "/product/gallery", component: GalleryView, props: { extraPadding: 2 }, errorElement: routeErrorFallback },
	{ path: "/product/gallery/category", component: CategoryView, props: { extraPadding: 2 } },
	{ path: "/product/edit", component: EditorView, props: { scrollHeadlines: true, scrollSnap: true }, errorElement: routeErrorFallback },
	{ path: "/product/gallery/detail/:id", component: WaterDrainageDetailView, props: { extraPadding: 2 } },
	{ path: "/search", component: SearchView, props: { extraPadding: 2 } },
	{ path: "/foto", component: FotoProductView, props: { extraPadding: 2 } },
	{ path: "/ritaproduct", component: RitaProductView, guard: (access) => access.poiA },
	{ path: "/infasting/gallery/detail/:id", component: InfastingDetailView, props: { extraPadding: 2 } },
	{ path: "/övrigt/gallery/detail/:id", component: OvrigtDetailView, props: { extraPadding: 2 } },

	// Settings
	{ path: "/settings", component: SettingsView },
	{ path: "/settings/personaldata", component: PersonaldataView },
	{ path: "/settings/changepassword", component: ChangePasswordForm },
	{ path: "/settings/companyinfo", component: CompanyinfoView },
	{ path: "/settings/financeinfo", component: FinancesInfoView },
	{ path: "/settings/user-terms-and-guidelines", component: UserTermsAndGuidelines },
	{ path: "/settings/statistikinfo", component: StatistikInfoView },
	{ path: "/settings/summary", component: AllProjectSummaryView },
	{ path: "/settings/inställnings-post", component: InställningspostView },
	{ path: "/settings/language-selection-page", component: LanguageSelectionPage },
	{ path: "/settings/inställnings-vinkelfunktion", component: InställningsGraderView },
];

export const customerRedirects: RedirectEntry[] = [
	{ from: "/project", to: "/project/list" },
	{ from: "/product", to: "/product/gallery" },
];
